import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Box, ButtonBase } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { useParams } from "react-router-dom";

import { linkShareItems } from "@/components/share/entityShareItems";
import { ShareMenu } from "@/components/share/ShareMenu";
import { ShareToChatDialog } from "@/components/share/ShareToChatDialog";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { toast } from "@/components/ui/use-toast";

import { salesApi } from "../client.js";
import { SalesErrorState } from "../components/SalesEmptyState.js";
import { Body } from "./lead-page/Body.js";
import { Hero } from "./lead-page/Hero.js";
import { LifecycleBar } from "./lead-page/LifecycleBar.js";
import { PrintHeader, PrintStyles } from "./lead-page/PrintLayer.js";
import { QualityPanel } from "./lead-page/QualityPanel.js";
import { buildLeadPdf, leadPdfFilename } from "./leadPdf.js";
import { ShareLeadDialog } from "./ShareLeadDialog.js";
import { salesLeadShareSubject } from "./shareSubject.js";

/**
 * Full-page lead detail.
 *
 * Routed via `/sales/leads/:id` in App.tsx; the leads list lives at
 * `/sales/leads` and never overlays anything. This file is the route
 * shell only — the sections it composes live as focused modules under
 * `./lead-page/`:
 *
 *   - `Hero`         — identity + confirmation verdict + TL;DR
 *   - `LifecycleBar` — operator pipeline moves (status / Qualify for CRM)
 *   - `QualityPanel` — the "why it's confirmed" centerpiece
 *   - `Body`         — 50/50 why-now (left) + outreach/contact (right)
 *   - `PrintLayer`   — print-only meta header + `@media print` stylesheet
 *   - `primitives` / `badges` / `leadTldr` — shared building blocks
 *
 * **Reading hierarchy:** a 5-second scan answers "is this a touch?", a
 * 30-second scan gives a copy-pasteable outreach.
 *
 * **Export to PDF + Share both build the SAME vector PDF.** The "Export PDF"
 * download and the "Share via email" attachment both call `buildLeadPdf(lead)`
 * (`leads/leadPdf.ts`) — a true VECTOR document drawn directly with jsPDF from
 * the lead data, NOT a screenshot of this DOM. That gives crisp, selectable
 * text at readable sizes, real page breaks that never split a row, a tiny file,
 * and identical output in the Tauri desktop WebKit shell. The two artifacts are
 * byte-for-byte identical because they share the one generator. The `no-print`
 * / `print-only` / `print-collapsed-show` / `data-print-expand` class hooks are
 * consumed by `PrintStyles`' `@media print` block so a plain browser Ctrl+P of
 * the page keeps working.
 */
export function LeadPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [shareOpen, setShareOpen] = useState(false);
  const [chatShareOpen, setChatShareOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const lead = useQuery({
    queryKey: ["sales", "leads", id],
    queryFn: () => salesApi.getLead(id!),
    enabled: !!id,
    refetchInterval: 60_000,
  });

  // Single source of the lead PDF: a true vector document drawn from the lead
  // data (NOT a page screenshot). Shared by the Export download AND the Share
  // email so the two are byte-for-byte identical. Passed to ShareLeadDialog as
  // the `getPdf` prop. Async to match that prop's `() => Promise<Blob>` shape.
  const capturePdf = async (): Promise<Blob> => {
    if (!lead.data) throw new Error("Lead content isn't ready yet — try again in a moment.");
    return buildLeadPdf(lead.data);
  };

  const onExport = async () => {
    if (!lead.data || exporting) return;
    setExporting(true);
    try {
      const blob = await capturePdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = leadPdfFilename(lead.data);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Could not generate the PDF.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      {/* Slim action toolbar only — the company name lives ONCE, in the Hero
          card below (no duplicate page title). */}
      <Box
        className="no-print"
        sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}
      >
        <BackButton to="/sales/leads" label="Back to leads" />
        <ButtonBase
          onClick={onExport}
          disabled={!lead.data || exporting}
          title="Download this lead as a PDF (the same PDF the Share email attaches)."
          data-testid="export-lead-pdf"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.default",
            px: 1.5,
            py: 0.75,
            fontSize: 12,
            fontWeight: 500,
            color: "text.primary",
            "&:hover": { bgcolor: "surfaceSoft" },
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {exporting ? "Exporting…" : "Export PDF"}
        </ButtonBase>
        <ShareMenu
          disabled={!lead.data}
          items={[
            {
              key: "email",
              label: "Share via email",
              icon: <EmailOutlinedIcon fontSize="small" />,
              testId: "share-menu-email",
              onClick: () => setShareOpen(true),
            },
            {
              key: "chat",
              label: "Share to chat",
              icon: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
              testId: "share-menu-chat",
              onClick: () => setChatShareOpen(true),
            },
            // "Copy link" (+ native share where supported) so the lead can be
            // dropped into any other app. The lead keeps its bespoke
            // branded-PDF "Share via email" above — these are the link-only
            // actions on top of it.
            ...(lead.data
              ? linkShareItems({
                  subject: salesLeadShareSubject(lead.data),
                  notify: (m) => toast(m),
                })
              : []),
          ]}
        />
      </Box>

      {!id ? (
        <SalesErrorState
          error={new Error("Missing lead id in URL")}
          surface="leads"
        />
      ) : lead.isLoading ? (
        <PageLoader />
      ) : lead.isError ? (
        <SalesErrorState error={lead.error} surface="leads" />
      ) : lead.data ? (
        <Box component="article" className="lead-print" sx={{ display: "grid", gap: 2.5 }}>
          <PrintHeader lead={lead.data} />
          <Hero lead={lead.data} />
          <LifecycleBar lead={lead.data} />
          <QualityPanel lead={lead.data} />
          <Body lead={lead.data} />
        </Box>
      ) : null}

      <PrintStyles />

      {lead.data ? (
        <>
          <ShareLeadDialog open={shareOpen} onClose={() => setShareOpen(false)} lead={lead.data} getPdf={capturePdf} />
          <ShareToChatDialog
            open={chatShareOpen}
            onClose={() => setChatShareOpen(false)}
            subject={salesLeadShareSubject(lead.data)}
          />
        </>
      ) : null}
    </Box>
  );
}
